import { token } from '@atlaskit/tokens';

import { type SelectionMode, type SelectionRange } from '../../../machine/state/types';
import { HAS_ACTIVE_HIGHLIGHT_CLASS, SELECTION_HIGHLIGHT_CLASS } from '../constants';
import { getBoxGeometry } from '../utils/get-box-geometry';

const CELL_HIGHLIGHT_Z_INDEX = 10;

export class HighlightsRenderer {
  #visibleCount = 0;

  constructor(
    private readonly renderTarget: HTMLElement,
    private readonly scrollContainer: HTMLElement,
    private readonly elements: Map<string, HTMLElement>
  ) {}

  #updateHighlightClass() {
    if (this.#visibleCount > 0) {
      this.scrollContainer.classList.add(HAS_ACTIVE_HIGHLIGHT_CLASS);
    } else {
      this.scrollContainer.classList.remove(HAS_ACTIVE_HIGHLIGHT_CLASS);
    }
  }

  /**
   * Creates a new highlight element for a selection range and mounts it to the DOM.
   *
   * @param range - The selection data (ID, anchor, lead).
   * @param mode - The current selection mode (cell, row, column), used to determine geometry.
   */
  add(range: SelectionRange, mode: SelectionMode) {
    // Idempotency Check
    // If we already track this ID, we don't create a duplicate DOM node.
    // Instead, we just ensure its position is up to date.
    if (this.elements.has(range.id)) {
      this.update(range, mode);

      return;
    }

    // Creation
    // Create the raw <div> element. At this point, it has no size or position.
    const el = this.#createHighlightsDiv(range.id);

    // Mounting
    this.renderTarget.appendChild(el);
    this.elements.set(range.id, el);

    // Initial Positioning
    // CRITICAL: We call update() synchronously immediately after mounting.
    // If we skipped this, the browser might paint the new <div> at (0,0) with
    // 0x0 size for one frame (a visual glitch) before the next update cycle.
    // Note: update() handles the visible count and class management.
    this.update(range, mode);
  }

  /**
   * Recalculates the position and dimensions of an existing highlight.
   *
   * @param range - The selection data to measure.
   * @param mode - The current mode, which dictates how we calculate bounds (e.g. full width for Rows).
   */
  update(range: SelectionRange, mode: SelectionMode) {
    const el = this.elements.get(range.id);

    if (!el) {
      return;
    }

    const wasVisible = el.style.display === 'block';

    // We pass the whole range + mode so the helper can determine the correct
    // bounding box strategy (e.g., Row Mode ignores the X columns and spans full width).
    const coords = getBoxGeometry(range, this.scrollContainer, mode);

    if (coords) {
      // The target cells are in the DOM (visible). Apply the transform.
      el.style.transform = `translate(${coords.x}px, ${coords.y}px)`;
      el.style.width = `${coords.width}px`;
      el.style.height = `${coords.height}px`;
      el.style.display = 'block';

      if (!wasVisible) {
        this.#visibleCount++;
        this.#updateHighlightClass();
      }
    } else {
      // VIRTUALIZATION HANDLING:
      // If coords is null, it usually means the target cells (e.g., Row 1000)
      // have been virtualized out of the DOM because the user scrolled away.
      //
      // We do NOT remove the highlight element here. The selection state still exists.
      // We simply hide it. When the user scrolls the cells back into view,
      // this method will run again, find coordinates, and unhide it.
      el.style.display = 'none';

      if (wasVisible) {
        this.#visibleCount--;
        this.#updateHighlightClass();
      }
    }
  }

  /**
   * Permanently removes a highlight from the DOM and memory.
   * Used when a selection range is actually deselected/deleted by the user.
   */
  remove(id: string) {
    const el = this.elements.get(id);

    if (el) {
      if (el.style.display === 'block') {
        this.#visibleCount--;
      }
      el.remove();
      this.elements.delete(id);
      this.#updateHighlightClass();
    }
  }

  /**
   * Wipes all highlights.
   * Used when replacing the entire selection state (e.g., clicking a new cell).
   */
  clear() {
    this.elements.forEach((el) => el.remove());
    this.elements.clear();
    this.#visibleCount = 0;
    this.#updateHighlightClass();
  }

  /**
   * Create the DOM node for the highlight element.
   */
  #createHighlightsDiv(id: string): HTMLElement {
    const highlightsDiv = document.createElement('div');
    const highlightFill = token('color.background.accent.blue.subtlest', '#E9F2FF');

    highlightsDiv.id = `highlight-${id}`;
    highlightsDiv.classList.add(SELECTION_HIGHLIGHT_CLASS);
    highlightsDiv.style.position = 'absolute';
    highlightsDiv.style.display = 'none';
    highlightsDiv.style.boxSizing = 'border-box';
    highlightsDiv.style.border = `2px solid ${token('color.border.selected', '#0C66E4')}`;
    highlightsDiv.style.background = `color-mix(in srgb, ${highlightFill} 25%, transparent)`;
    highlightsDiv.style.borderRadius = token('radius.small', '3px');
    highlightsDiv.style.pointerEvents = 'none';
    highlightsDiv.style.zIndex = `${CELL_HIGHLIGHT_Z_INDEX}`;

    return highlightsDiv;
  }
}
